import { useEffect, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  courseGrade,
  gradeChipLabel,
  formatTimeRange,
  type Course,
  type SyllabusExtract,
} from '@app/shared'

import {
  useAnnouncements,
  useAssignments,
  useClock,
  useCourse,
  useLectures,
  useSyllabus,
  useSyncStatus,
  useTerm,
} from '../lib/data'
import { gradeRise } from '../lib/motion'
import { DataModeBadge } from '../components/HeaderBand'
import { SkeletonRows } from '../components/Card'
import { Doc, Lock } from '../components/school/icons'
import {
  Breadcrumb,
  CanvasSyncChip,
  SchoolBand,
  SchoolSearchField,
  Unavailable,
} from '../components/school/Chrome'
import { AssignmentsTable } from '../components/school/AssignmentsTable'
import { LectureList } from '../components/school/LectureList'
import { SyllabusPanel } from '../components/school/SyllabusPanel'
import { CourseAnnouncementsPanel } from '../components/school/AnnouncementList'
import { hueBg } from '../components/school/derive'

/**
 * COURSE PAGE — docs/design/handoff/"LifeOS School.dc.html", option 6b.
 *
 * Breadcrumb, a course header carrying the grade state IN WORDS, the assignments table, the
 * lectures list with its expandable AI summary, and a right column of syllabus + announcements.
 *
 * The mockup is COGSCI 175 specifically because that is the course whose instructor hid grades:
 * the header says "grade hidden by instructor" rather than rendering an empty score slot. The
 * grade-visible variant of the same header is a specimen under the frame in the mockup, and it is
 * the same component here — one <CourseHeader/>, three states, no special case.
 */
export function SchoolCourse() {
  const { courseCode } = useParams<{ courseCode: string }>()
  const { data: clock } = useClock()
  const { data: term } = useTerm()
  const { data: course, isPending: coursePending, error: courseError } = useCourse(courseCode)
  const {
    data: assignments,
    isPending: assignmentsPending,
    error: assignmentsError,
  } = useAssignments({ courseId: course?.id })
  const { data: lectures, isPending: lecturesPending, error: lecturesError } = useLectures(course?.id)
  const { data: syllabus, isPending: syllabusPending, error: syllabusError } = useSyllabus(course?.id)
  const {
    data: announcements,
    isPending: announcementsPending,
    error: announcementsError,
  } = useAnnouncements({ courseId: course?.id })
  const { data: sync, error: syncError } = useSyncStatus()

  const today = clock?.date
  const nowMinutes = clock?.minutesIntoDay ?? 0

  return (
    <>
      <SchoolBand>
        <Breadcrumb to="/school" parent="School" title={course?.code ?? courseCode ?? 'Course'} />
        <SchoolSearchField
          placeholder="Search this course"
          scope={course?.code}
          maxWidth={300}
        />
        {today && (
          <CanvasSyncChip rows={sync} error={syncError} today={today} nowMinutes={nowMinutes} />
        )}
        <DataModeBadge />
      </SchoolBand>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-[30px] pb-[26px]">
        {coursePending && (
          <div className="rounded-context border border-line bg-surface px-5 py-[18px] shadow-card">
            <SkeletonRows rows={2} />
          </div>
        )}
        <Unavailable error={courseError} />

        {course === null && !coursePending && (
          <div className="rounded-context border border-line bg-surface px-5 py-[18px] shadow-card">
            <p className="text-body text-ink-3 text-pretty">
              No course matches “{courseCode}”. It may have been dropped, or the address may be
              out of date. <Link to="/school" className="underline underline-offset-2">Back to School</Link>.
            </p>
          </div>
        )}

        {course && today && (
          <>
            <CourseHeader course={course} syllabus={syllabus ?? null} />

            <div className="flex min-h-0 flex-1 gap-3 overflow-hidden">
              <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-[14px] overflow-y-auto">
                <AssignmentsTable
                  assignments={assignments}
                  course={course}
                  isPending={assignmentsPending}
                  error={assignmentsError}
                />
                <LectureList
                  lectures={lectures}
                  isPending={lecturesPending}
                  error={lecturesError}
                  today={today}
                />
              </div>

              <div className="flex w-[352px] min-h-0 flex-none flex-col gap-[14px] overflow-y-auto">
                <SyllabusPanel
                  syllabus={syllabus}
                  courseId={course.id}
                  term={term}
                  isPending={syllabusPending}
                  error={syllabusError}
                  today={today}
                />
                <CourseAnnouncementsPanel
                  announcements={announcements}
                  isPending={announcementsPending}
                  error={announcementsError}
                  today={today}
                  nowMinutes={nowMinutes}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}

/**
 * THE HEADER, AND THE HIDDEN-GRADE STATE.
 *
 * The three grade states are three different boxes with three different sentences:
 *
 *   score   green box   'CURRENT SCORE'  ·  '92.1% · A−'
 *   hidden  grey box + padlock           ·  'grade hidden by instructor'
 *   none    grey box                     ·  'nothing graded yet'
 *
 * The middle one is the whole reason this screen was designed around COGSCI 175. Canvas returns
 * nothing when an instructor sets `hide_final_grades`, and the tempting implementations are an
 * empty box (reads as "you have no grade") or no box at all (reads as "this course has no
 * grades"). Both are false. The box stays, and it says what happened.
 */
function CourseHeader({
  course,
  syllabus,
}: {
  course: Course
  syllabus: SyllabusExtract | null
}) {
  const grade = courseGrade(course)

  // 13f · A GRADE LANDS. The trigger is real and it is on this page: the header's own Canvas chip
  // runs `useSyncConnector`, which invalidates `['course']` — so a sync started three inches above
  // this box can replace the score under it. When it does, the box rises `translateY(9px) → 0`.
  //
  // ONE ELEMENT, NOT TWO. The brief's rule is that "a score never arrives without context": the
  // label and the number are inside the same box and rise in the SAME BEAT, because a number that
  // lands a moment before what it means is a number without context. (The brief's class median is
  // deliberately absent — Canvas returns none, and inventing one would be the app faking
  // knowledge.)
  //
  // FIRST PAINT DOES NOT RISE. A score that is on screen because the page just opened has not
  // landed; it was already there. Same discipline as `ValueSwap` and the sync dot.
  const scoreLabel = grade.kind === 'score' ? gradeChipLabel(grade) : null
  const scoreBox = useRef<HTMLDivElement>(null)
  const seen = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    const was = seen.current
    seen.current = scoreLabel
    if (was === undefined || was === scoreLabel || scoreLabel === null) return
    const run = gradeRise(scoreBox.current)
    return () => run.cancel()
  }, [scoreLabel])

  return (
    <section className="flex flex-none items-center gap-4 rounded-context border border-line bg-surface px-5 py-[13px] shadow-card">
      <span
        className={['w-[5px] flex-none self-stretch rounded-[3px]', hueBg(course.code)].join(' ')}
        aria-hidden
      />
      <div className="flex min-w-0 flex-1 flex-col gap-[7px]">
        <div className="flex items-baseline gap-[11px]">
          <h1 className="text-section font-semibold">{course.code}</h1>
          <span className="text-[14px] font-normal leading-none text-ink-2">{course.name}</span>
        </div>
        <div className="flex flex-wrap items-center gap-[10px] text-[12px] font-normal leading-none text-muted">
          {/* Canvas has no instructor name until the connector runs, so the slot is skipped
              rather than filled with 'Instructor'. */}
          {course.instructor && <span>{course.instructor}</span>}
          {course.instructor && <Dot />}
          <span>{meetingPattern(course)}</span>
          <Dot />
          {/* Locations and credit hours are Canvas absences, not blanks: the course object
              carries neither, so the segment is dropped rather than rendered empty. */}
          {course.meetings.length > 0 && (
            <>
              <span>{[...new Set(course.meetings.map((m) => m.location))].join(' · ')}</span>
              <Dot />
            </>
          )}
          {course.units !== null && (
            <>
              <span>{course.units} units</span>
              <Dot />
            </>
          )}
          {/* Whether a syllabus has actually been read for this course — `getSyllabus`
              returning null is a real, sayable answer (none read yet), not a loading gap, so the
              chip is absent rather than a false claim (hidden ≠ missing).

              The WORD follows what was read. The handoff says "Syllabus PDF" because that is what
              a syllabus usually is; Canvas hands this app the instructor's typed HTML and never a
              PDF, and a chip claiming a PDF nobody opened is a small lie on the one screen whose
              subject is telling synced facts from machine-made ones. */}
          {syllabus && (
            <span className="flex items-center gap-[5px] font-medium text-success-dot">
              <Doc size={12} strokeWidth={1.9} />
              {/\.pdf$/i.test(syllabus.sourceFile.trim()) ? 'Syllabus PDF' : 'Syllabus read'}
            </span>
          )}
        </div>
      </div>

      {grade.kind === 'score' ? (
        <div
          ref={scoreBox}
          className="flex flex-none items-center gap-[9px] rounded-row border border-success-line bg-success-fill px-[13px] py-[7px]"
        >
          <div className="flex flex-col gap-[3px]">
            <span className="text-[9px] font-semibold uppercase leading-none tracking-[0.08em] text-success">
              CURRENT SCORE
            </span>
            <span className="text-[15px] font-semibold leading-none text-success">
              {gradeChipLabel(grade).replace(' ', ' · ')}
            </span>
          </div>
        </div>
      ) : (
        <div className="flex flex-none items-center gap-[9px] rounded-row border border-line-3 bg-surface-4 px-[13px] py-[7px]">
          {grade.kind === 'hidden' && (
            <Lock size={14} strokeWidth={1.8} className="flex-none text-microcopy" />
          )}
          <div className="flex flex-col gap-[3px]">
            <span className="text-[9px] font-semibold uppercase leading-none tracking-[0.08em] text-microcopy">
              CURRENT SCORE
            </span>
            {/* 13f's other half: THE SHIMMER THAT NEVER RESOLVES. A slow opacity breath, 3s,
                indefinitely — the one animation in the app that resolves into nothing, because
                there is nothing to resolve into. Not a spinner, not a dash, not an empty cell: a
                spinner would promise an arrival, and nothing is arriving.

                On the VALUE only. "CURRENT SCORE" above it is a label, not withheld data, and the
                padlock is the reason rather than the datum; breathing the whole box would say the
                question was withheld too. `not graded yet` stays perfectly still — it is a
                settled fact, not a fact being kept from him. */}
            <span
              className={[
                'text-[13px] font-medium leading-none text-muted',
                grade.kind === 'hidden' ? 'mo-withheld' : '',
              ].join(' ')}
            >
              {grade.kind === 'hidden' ? 'grade hidden by instructor' : 'nothing graded yet'}
            </span>
          </div>
        </div>
      )}
    </section>
  )
}

function Dot() {
  return <span className="size-[3px] flex-none rounded-full bg-dash" aria-hidden />
}

/**
 * 'MW 10:00–11:30' — the lecture pattern, built from the meetings Canvas actually returned
 * rather than stored as a string. The campus shorthand: Tuesday and Thursday take two letters so
 * they cannot be confused with each other, everything else takes one.
 */
const DAY_LETTER = ['Su', 'M', 'Tu', 'W', 'Th', 'F', 'Sa']

function meetingPattern(course: Course): string {
  const lectures = course.meetings.filter((m) => m.kind === 'lecture')
  // Berkeley's meeting times are not in Canvas at all (docs/research/berkeley-data-sources.md
  // §B.7), so an empty pattern means "not known here" and never "this course does not meet".
  if (course.meetings.length === 0) return 'no meeting times in Canvas'
  if (lectures.length === 0) return 'no scheduled lecture'
  const days = [...new Set(lectures.map((m) => m.weekday))]
    .sort((a, b) => a - b)
    .map((wd) => DAY_LETTER[wd])
    .join('')
  const first = lectures[0]!
  return `${days} ${formatTimeRange(first.startMinutes, first.endMinutes)}`
}
